import { BillingInterval, SelfServicePlanCode } from './SaasApi';

const SIGNUP_DRAFT_SESSION_KEY = 'superpenguin:self-serve-signup-draft';
const SIGNUP_DRAFT_LOCAL_PREFIX = `${SIGNUP_DRAFT_SESSION_KEY}:`;
const SIGNUP_DRAFT_MAX_AGE_MS = 24 * 60 * 60 * 1000;
const SIGNUP_DRAFT_ID_PATTERN = /^[A-Za-z0-9_-]{20,80}$/;
const SIGNUP_VERSION_PATTERN = /^[A-Za-z0-9._-]{1,120}$/;
const selfServicePlanCodes = new Set<SelfServicePlanCode>(['starter', 'premium']);
const MAX_SELF_SERVICE_UNITS = 20;

export type SignupDraft = {
    draftId: string;
    planCode: SelfServicePlanCode;
    versionId: string;
    interval: BillingInterval;
    subscriptionUnits: number;
    email: string;
    ownerName: string;
    providerName: string;
    accountCreated: boolean;
};

type StoredSignupDraft = SignupDraft & {
    savedAt: number;
};

// Generate an opaque lookup key so the verification URL never contains clinic PII.
export const createSignupDraftId = (): string => {
    const cryptoApi = window.crypto;
    if (typeof cryptoApi?.randomUUID === 'function') {
        return cryptoApi.randomUUID().replace(/-/g, '');
    }
    const bytes = new Uint8Array(18);
    if (typeof cryptoApi?.getRandomValues === 'function') {
        cryptoApi.getRandomValues(bytes);
        return Array.from(bytes, (value) => value.toString(16).padStart(2, '0')).join('');
    }
    // This ID is only a local lookup key, but retain a compatible fallback for
    // browsers that block the Web Crypto API.
    return `${Date.now().toString(36)}${Math.random().toString(36).slice(2)}${Math.random().toString(36).slice(2)}`
        .replace(/[^A-Za-z0-9_-]/g, '')
        .padEnd(20, '0')
        .slice(0, 80);
};

const isCurrentStoredDraft = (draft: StoredSignupDraft): boolean =>
    Number.isFinite(draft.savedAt) &&
    draft.savedAt <= Date.now() + 60_000 &&
    Date.now() - draft.savedAt <= SIGNUP_DRAFT_MAX_AGE_MS;

// Decode and validate every field before a locally stored draft influences signup.
const parseStoredSignupDraft = (serialized: string | null): StoredSignupDraft | null => {
    if (!serialized) return null;
    try {
        const parsed = JSON.parse(serialized) as Partial<StoredSignupDraft> | null;
        if (!parsed ||
            typeof parsed.draftId !== 'string' || !SIGNUP_DRAFT_ID_PATTERN.test(parsed.draftId) ||
            !selfServicePlanCodes.has(parsed.planCode as SelfServicePlanCode) ||
            typeof parsed.versionId !== 'string' || !SIGNUP_VERSION_PATTERN.test(parsed.versionId) ||
            (parsed.interval !== 'monthly' && parsed.interval !== 'annual') ||
            typeof parsed.email !== 'string' || typeof parsed.ownerName !== 'string' ||
            typeof parsed.providerName !== 'string' || typeof parsed.accountCreated !== 'boolean' ||
            typeof parsed.savedAt !== 'number') {
            return null;
        }
        // Older Free trial drafts predate selectable units and safely map to their fixed single unit.
        const subscriptionUnits = parsed.subscriptionUnits === undefined && parsed.planCode === 'starter'
            ? 1
            : parsed.subscriptionUnits;
        if (!Number.isInteger(subscriptionUnits) || (subscriptionUnits as number) < 1 ||
            (subscriptionUnits as number) > MAX_SELF_SERVICE_UNITS ||
            (parsed.planCode === 'starter' && subscriptionUnits !== 1)) {
            return null;
        }
        const draft = { ...parsed, subscriptionUnits } as StoredSignupDraft;
        return isCurrentStoredDraft(draft) ? draft : null;
    } catch {
        return null;
    }
};

const readSessionDraft = (): StoredSignupDraft | null => {
    try {
        return parseStoredSignupDraft(window.sessionStorage.getItem(SIGNUP_DRAFT_SESSION_KEY));
    } catch {
        return null;
    }
};

const readLocalDraft = (draftId: string): StoredSignupDraft | null => {
    if (!SIGNUP_DRAFT_ID_PATTERN.test(draftId)) return null;
    try {
        const draft = parseStoredSignupDraft(
            window.localStorage.getItem(`${SIGNUP_DRAFT_LOCAL_PREFIX}${draftId}`)
        );
        return draft?.draftId === draftId ? draft : null;
    } catch {
        return null;
    }
};

// Read the original tab's draft, or an exact opaque draft selected by the
// verification continuation URL. Plan fields must agree with the current URL.
export const loadSignupDraft = (
    planCode: SelfServicePlanCode | null,
    versionId: string | null,
    interval: BillingInterval | null,
    resumeDraftId?: string | null,
    subscriptionUnits?: number | null
): SignupDraft | null => {
    if (!planCode || !versionId || !interval) return null;
    const storedDraft = resumeDraftId ? readLocalDraft(resumeDraftId) : readSessionDraft();
    if (!storedDraft || storedDraft.planCode !== planCode ||
        storedDraft.versionId !== versionId || storedDraft.interval !== interval ||
        (subscriptionUnits !== undefined && subscriptionUnits !== null &&
            storedDraft.subscriptionUnits !== subscriptionUnits)) {
        return null;
    }

    // Copy a verified cross-tab draft into this tab for subsequent navigation.
    try {
        window.sessionStorage.setItem(SIGNUP_DRAFT_SESSION_KEY, JSON.stringify(storedDraft));
    } catch {
        // The exact local draft remains available when session storage is blocked.
    }
    const {savedAt: _savedAt, ...draft} = storedDraft;
    return draft;
};

// Passwords and Firebase credentials are deliberately excluded from this draft.
export const saveSignupDraft = (draft: SignupDraft): void => {
    if (!SIGNUP_DRAFT_ID_PATTERN.test(draft.draftId) ||
        !Number.isInteger(draft.subscriptionUnits) || draft.subscriptionUnits < 1 ||
        draft.subscriptionUnits > MAX_SELF_SERVICE_UNITS ||
        (draft.planCode === 'starter' && draft.subscriptionUnits !== 1)) return;
    const serialized = JSON.stringify({...draft, savedAt: Date.now()});
    try {
        window.sessionStorage.setItem(SIGNUP_DRAFT_SESSION_KEY, serialized);
    } catch {
        // The verification continuation can still use local storage.
    }
    if (draft.accountCreated) {
        try {
            window.localStorage.setItem(`${SIGNUP_DRAFT_LOCAL_PREFIX}${draft.draftId}`, serialized);
        } catch {
            // The original signup tab can still resume from session storage.
        }
    }
};

export const clearSignupDraft = (draftId?: string | null): void => {
    const sessionDraft = readSessionDraft();
    try {
        window.sessionStorage.removeItem(SIGNUP_DRAFT_SESSION_KEY);
    } catch {
        // Nothing else is required when session storage is unavailable.
    }
    const clearedDraftId = draftId || sessionDraft?.draftId || '';
    if (!SIGNUP_DRAFT_ID_PATTERN.test(clearedDraftId)) return;
    try {
        window.localStorage.removeItem(`${SIGNUP_DRAFT_LOCAL_PREFIX}${clearedDraftId}`);
    } catch {
        // The short-lived draft expires automatically when local storage is unavailable.
    }
};

// Recover the newest exact signup attempt only when it belongs to the signed-in email.
export const getSignupResumePath = (accountEmail: string | null): string | null => {
    const normalizedAccountEmail = (accountEmail || '').trim().toLowerCase();
    if (!normalizedAccountEmail) return null;

    const candidates: StoredSignupDraft[] = [];
    const sessionDraft = readSessionDraft();
    if (sessionDraft) candidates.push(sessionDraft);
    try {
        for (let index = 0; index < window.localStorage.length; index += 1) {
            const key = window.localStorage.key(index);
            if (!key?.startsWith(SIGNUP_DRAFT_LOCAL_PREFIX)) continue;
            const draft = parseStoredSignupDraft(window.localStorage.getItem(key));
            if (draft && key === `${SIGNUP_DRAFT_LOCAL_PREFIX}${draft.draftId}` &&
                !candidates.some((candidate) => candidate.draftId === draft.draftId)) {
                candidates.push(draft);
            }
        }
    } catch {
        // Session storage may still provide a same-tab recovery destination.
    }

    const draft = candidates
        .filter((candidate) => candidate.accountCreated &&
            candidate.email.trim().toLowerCase() === normalizedAccountEmail)
        .sort((left, right) => right.savedAt - left.savedAt)[0];
    if (!draft) return null;

    return `/signup?${new URLSearchParams({
        plan: draft.planCode,
        version: draft.versionId,
        interval: draft.interval,
        units: String(draft.subscriptionUnits),
        resume: '1',
        draft: draft.draftId,
    }).toString()}`;
};
